import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface ProgressiveDisclosureOption {
  id: string;
  label: string;
  prompt: string;
}

export interface ProgressiveDisclosureCategory {
  id: string;
  label: string;
  icon: BenchlingIconName;
  options: Array<ProgressiveDisclosureOption>;
}

export const PROGRESSIVE_DISCLOSURE_CATEGORIES: Array<ProgressiveDisclosureCategory> = [
  {
    id: 'capture-data',
    label: 'Capture data',
    icon: 'file-chart-column-outline',
    options: [
      {
        id: 'register-entities',
        label: 'Register entities from file',
        prompt:
          'I want to import entities into Benchling. Please ask me one or two questions to determine what entities need to be registered and any context on the data source to assist with registration. Then generate a draft entry with the appropriate registration tables for data capture.',
      },
      {
        id: 'import-results',
        label: 'Import results from file',
        prompt:
          'I want to import results into Benchling. Please ask me one or two questions to determine what results need to be created and any context on the data source to assist with import. Then generate a draft entry with the appropriate result tables for data capture.',
      },
      {
        id: 'create-notebook-entry',
        label: 'Create and fill a notebook entry',
        prompt:
          'I want to draft a notebook entry using AI. Start a draft prefilled with placeholder sections for a standard notebook entry and ask me one or two questions to determine what the purpose of the entry is, if it should follow a specific template, and any other specific needs for its structure.',
      },
    ],
  },
  {
    id: 'analyze-data',
    label: 'Analyze data',
    icon: 'magnifying-glass-chart-outline',
    options: [
      {
        id: 'statistical-analysis',
        label: 'Run statistical analysis on data',
        prompt:
          'I want to run statistical analyses on my recent result data. Please ask me one or two questions to confirm the type of statistical analysis and which data it should be run on.',
      },
      {
        id: 'chart-results',
        label: 'Chart and plot results',
        prompt:
          'I want to plot and chart my recent result data. Please ask me one or two questions to confirm what type of charts or plots are needed and which result data it should be run on.',
      },
      {
        id: 'qc-check',
        label: 'QC check my data',
        prompt:
          "I want to do QC checks on my recently captured data. Please ask me one or two questions to determine which data I'm interested in QC checking and any specific QC requirements I'm trying to compare against.",
      },
    ],
  },
  {
    id: 'experiment-design',
    label: 'Design experiments',
    icon: 'flask',
    options: [
      {
        id: 'design-screening-experiment',
        label: 'Design a screening experiment',
        prompt:
          'I want to design a structured experiment using a DoE approach. Please ask me one or two questions to determine what factors and responses I want to test, then draft an experimental design.',
      },
      {
        id: 'optimize-assay-process',
        label: 'Optimize an assay or process',
        prompt:
          'I want to optimize an assay or process by varying several factors at once. Please ask me one or two questions about my factors, their ranges, and how many runs I can fit, then draft an experimental design that finds the best conditions efficiently.',
      },
      {
        id: 'design-custom-experiment',
        label: 'Design a custom experiment',
        prompt:
          'I want to design an experiment. Please ask me one or two questions to understand my hypothesis and key variables, then draft a structured experimental plan.',
      },
    ],
  },
  {
    id: 'generate-hypotheses',
    label: 'Generate hypotheses',
    icon: 'lightbulb',
    options: [
      {
        id: 'investigate-unexpected-result',
        label: 'Investigate an unexpected result',
        prompt:
          "My results contradict what's reported in the literature. Please ask me one or two questions about my data and the expected mechanism, then reason across my notebook data and the published literature to generate a few plausible hypotheses, ranked by likelihood.",
      },
      {
        id: 'generate-mechanistic-hypotheses',
        label: 'Generate mechanistic hypotheses',
        prompt:
          "I want to generate hypotheses for why my target or pathway is behaving a certain way. Please ask me one or two questions about my system and observations, then draw on my program's data alongside the literature to draft a ranked list of plausible mechanisms with supporting rationale.",
      },
      {
        id: 'stress-test-hypothesis',
        label: 'Stress-test my hypothesis',
        prompt:
          'I have an initial hypothesis I want to pressure-test. Please ask me one or two questions about it, then check it against both my own prior experiments and the published literature to suggest alternative or refined hypotheses I may not have considered.',
      },
    ],
  },
  {
    id: 'write-reports',
    label: 'Write reports',
    icon: 'pen-to-square-outline',
    options: [
      {
        id: 'experimental-report',
        label: 'Write a lab report',
        prompt:
          'I want to write a lab report on my recent work. Please ask me one or two questions to determine what the report should be on, and any requirements there are for format, content or style.',
      },
      {
        id: 'regulatory-report',
        label: 'Write a regulatory report',
        prompt:
          "I want to write a regulatory report on the programs or projects I've been involved in. Please ask me one or two questions to determine the specific type of regulatory report needed, and confirm the scope and topic.",
      },
      {
        id: 'status-report',
        label: 'Write a status report',
        prompt:
          'I want to write a status report based on my recent work. Offer a suggestion for what this report could contain and ask me one or two questions to confirm the scope of the report and its intended audience.',
      },
    ],
  },
];
